import React, { useEffect, useState } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import {
  Radar, RadarChart, PolarGrid, PolarAngleAxis, ResponsiveContainer, PolarRadiusAxis,
} from "recharts";
import { useAuth } from "@/context/AuthContext";
import Header from "@/components/Header";
import { TRAITS, ACH_META, fmtMoney, fmtNum } from "@/data/gameData";
import { RefreshCw, Home as HomeIcon, Share2, Trophy, Users, TrendingUp, Loader2, Copy } from "lucide-react";

export default function Result() {
  const { sessionId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { http } = useAuth();
  const [result, setResult] = useState(location.state?.result || null);
  const [loading, setLoading] = useState(!location.state?.result);

  useEffect(() => {
    if (result) return;
    http.get(`/game/result/${sessionId}`).then(({ data }) => setResult(data)).catch(() => {}).finally(() => setLoading(false));
  }, [sessionId, http, result]);

  if (loading || !result) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        {loading ? <Loader2 className="w-8 h-8 animate-spin text-[#F43F5E]" /> : <p className="text-slate-400">Resultado não encontrado.</p>}
      </div>
    );
  }

  const maxTrait = Math.max(3, ...TRAITS.map((t) => result.traits?.[t.key] || 0));
  const radarData = TRAITS.map((t) => ({ trait: t.label.split(" ")[0], value: result.traits?.[t.key] || 0, full: maxTrait }));

  const unlockedAch = result.achievements || [];
  const cmp = result.comparison || {};

  const shareText =
    `Eu sou um ${result.persona?.nome} ${result.persona?.emoji}\n` +
    `Minha pontuação em "E Se Fosse Comigo?": ${fmtNum(result.score)}\n` +
    `E você? Qual seria sua escolha?`;

  const doShare = async () => {
    const url = window.location.origin;
    if (navigator.share) {
      try { await navigator.share({ title: "E Se Fosse Comigo?", text: shareText, url }); return; }
      catch (err) { console.debug("share cancelado", err); }
    }
    try { await navigator.clipboard.writeText(`${shareText}\n${url}`); toast.success("Resultado copiado! Cole onde quiser."); }
    catch (err) { console.error("clipboard falhou", err); toast.error("Não foi possível compartilhar."); }
  };

  const social = [
    { name: "WhatsApp", color: "#25D366", url: `https://wa.me/?text=${encodeURIComponent(shareText + " " + window.location.origin)}` },
    { name: "X", color: "#fff", url: `https://twitter.com/intent/tweet?text=${encodeURIComponent(shareText)}&url=${encodeURIComponent(window.location.origin)}` },
    { name: "Telegram", color: "#2AABEE", url: `https://t.me/share/url?url=${encodeURIComponent(window.location.origin)}&text=${encodeURIComponent(shareText)}` },
  ];

  return (
    <div className="min-h-screen">
      <Header />
      <div className="max-w-2xl mx-auto px-5 py-8 pb-16">

        {/* Persona hero */}
        <motion.div initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.5 }}
          data-testid="result-persona" className="text-center">
          <p className="text-xs uppercase tracking-[0.3em] text-slate-500">Seu perfil é</p>
          <div className="text-6xl my-3">{result.persona?.emoji}</div>
          <h1 className="font-display font-black text-4xl sm:text-5xl tracking-tighter text-[#F43F5E] text-glow">
            {result.persona?.nome}
          </h1>
          <p className="text-slate-300 mt-3 max-w-md mx-auto">{result.persona?.desc}</p>
          {result.analise && <p className="text-sm text-slate-300 italic mt-2">"{result.analise}"</p>}
        </motion.div>

        {/* Score + money */}
        <div className="grid grid-cols-2 gap-4 mt-8">
          <div data-testid="result-score" className="rounded-3xl p-6 bg-[#0F0F14] border border-white/10 text-center">
            <p className="text-xs uppercase tracking-widest text-slate-500">Pontuação</p>
            <p className="font-display font-black text-4xl text-[#F43F5E] mt-2">{fmtNum(result.score)}</p>
          </div>
          <div className="rounded-3xl p-6 bg-[#0F0F14] border border-white/10 text-center">
            <p className="text-xs uppercase tracking-widest text-slate-500">Dinheiro final</p>
            <p className="font-display font-black text-2xl text-[#10B981] mt-2 leading-tight">{fmtMoney(result.money)}</p>
            <p className="text-xs text-slate-500 mt-1">de {fmtMoney(result.money_start)}</p>
          </div>
        </div>

        {/* Ending narrative */}
        <div className="rounded-3xl p-6 bg-[#0F0F14] border border-white/10 mt-4">
          <p className="text-xs uppercase tracking-widest text-slate-500 mb-2">{result.titulo_final || "O desfecho"}</p>
          <p className="font-display text-lg leading-snug">{result.narrativa_final}</p>
        </div>

        {/* Decision style */}
        {result.estilo && (
          <div data-testid="result-estilo" className="rounded-3xl p-6 bg-[#0F0F14] border border-white/10 mt-4">
            <p className="text-xs uppercase tracking-widest text-slate-500 mb-2">Meu jeito de decidir</p>
            <p className="font-display font-black text-xl">{result.estilo.nome}</p>
            <p className="text-sm text-slate-300 mt-1">{result.estilo.label}</p>
          </div>
        )}

        {/* Radar */}
        <div className="rounded-3xl p-6 bg-[#0F0F14] border border-white/10 mt-4">
          <p className="text-xs uppercase tracking-widest text-slate-500 mb-2">Seus indicadores</p>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <RadarChart data={radarData} outerRadius="72%">
                <PolarGrid stroke="rgba(255,255,255,0.12)" />
                <PolarAngleAxis dataKey="trait" tick={{ fill: "#94A3B8", fontSize: 11 }} />
                <PolarRadiusAxis domain={[0, maxTrait]} tick={false} axisLine={false} />
                <Radar dataKey="value" stroke="#F43F5E" fill="#F43F5E" fillOpacity={0.45} />
              </RadarChart>
            </ResponsiveContainer>
          </div>
          <div className="grid grid-cols-2 gap-2 mt-2">
            {TRAITS.map((t) => (
              <div key={t.key} className="flex items-center justify-between text-sm">
                <span className="text-slate-400">{t.emoji} {t.label}</span>
                <span className="font-semibold" style={{ color: t.color }}>{result.traits?.[t.key] || 0}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Comparison (demo) */}
        <div data-testid="result-comparison" className="rounded-3xl p-6 bg-[#0F0F14] border border-white/10 mt-4">
          <div className="flex items-center gap-2 mb-3">
            <Users className="w-4 h-4 text-[#06B6D4]" />
            <p className="text-xs uppercase tracking-widest text-slate-500">Como você se compara?</p>
            <span className="ml-auto text-[10px] px-2 py-0.5 rounded-full bg-white/5 border border-white/10 text-slate-400">DEMONSTRAÇÃO</span>
          </div>
          <div className="space-y-3">
            <div className="flex items-center gap-3">
              <TrendingUp className="w-4 h-4 text-[#F59E0B] shrink-0" />
              <p className="text-sm text-slate-300">Você escolheu diferente de <b className="text-white">{cmp.escolha_diferente}%</b> dos jogadores.</p>
            </div>
            <div className="flex items-center gap-3">
              <Trophy className="w-4 h-4 text-[#8B5CF6] shrink-0" />
              <p className="text-sm text-slate-300">Você está entre os <b className="text-white">{cmp.percentil_traco}%</b> mais {(cmp.traco_nome || "").toLowerCase()}s.</p>
            </div>
          </div>
          <p className="text-xs text-slate-400 mt-3">* Dados de demonstração. Estatísticas reais de jogadores chegam em breve.</p>
        </div>

        {/* Achievements */}
        <div data-testid="result-achievements" className="rounded-3xl p-6 bg-[#0F0F14] border border-white/10 mt-4">
          <p className="text-xs uppercase tracking-widest text-slate-500 mb-3">Conquistas desbloqueadas</p>
          {unlockedAch.length ? (
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
              {unlockedAch.map((id) => <AchBadge key={id} id={id} />)}
            </div>
          ) : <p className="text-sm text-slate-500">Nenhuma nova conquista desta vez.</p>}
        </div>

        {/* Share */}
        <div className="rounded-3xl p-6 bg-gradient-to-br from-[#F43F5E]/15 to-[#8B5CF6]/10 border border-[#F43F5E]/25 mt-4 text-center">
          <p className="font-display font-black text-xl">Compartilhe seu resultado</p>
          <p className="text-sm text-slate-300 mt-1">"Eu sou um {result.persona?.nome} {result.persona?.emoji}"</p>
          <button data-testid="result-share-btn" onClick={doShare}
            className="mt-4 w-full flex items-center justify-center gap-2 rounded-2xl py-3.5 bg-[#F43F5E] hover:bg-[#E11D48] active:scale-95 transition-all font-semibold shadow-lg shadow-[#F43F5E]/30">
            <Share2 className="w-4 h-4" /> Compartilhar meu resultado
          </button>
          <div className="flex gap-2 mt-3">
            {social.map((s) => (
              <a key={s.name} data-testid={`share-${s.name.toLowerCase()}`} href={s.url} target="_blank" rel="noreferrer"
                className="flex-1 py-2.5 rounded-xl bg-white/5 border border-white/10 text-sm font-medium hover:bg-white/10 transition-colors" style={{ color: s.color }}>
                {s.name}
              </a>
            ))}
          </div>
        </div>

        {/* Replay */}
        <div className="mt-6 text-center">
          <p className="font-display text-lg text-slate-300">E se você tivesse escolhido diferente?</p>
          <div className="grid grid-cols-2 gap-3 mt-4">
            <button data-testid="result-replay-btn" onClick={() => navigate(`/jogar/${result.category}`)}
              className="flex items-center justify-center gap-2 rounded-2xl py-3.5 bg-white/5 border border-white/10 hover:bg-white/10 active:scale-95 transition-all font-semibold">
              <RefreshCw className="w-4 h-4" /> Jogar novamente
            </button>
            <button data-testid="result-home-btn" onClick={() => navigate("/")}
              className="flex items-center justify-center gap-2 rounded-2xl py-3.5 bg-white/5 border border-white/10 hover:bg-white/10 active:scale-95 transition-all font-semibold">
              <HomeIcon className="w-4 h-4" /> Início
            </button>
          </div>
          <button onClick={() => navigate("/historias")} className="mt-3 text-sm text-[#06B6D4] flex items-center gap-1 mx-auto">
            Tentar outra categoria
          </button>
          <button data-testid="result-ranking-btn" onClick={() => navigate("/ranking")}
            className="mt-4 w-full flex items-center justify-center gap-2 rounded-2xl py-3.5 bg-[#FACC15]/10 border border-[#FACC15]/30 text-[#FACC15] font-semibold hover:bg-[#FACC15]/20 transition-colors">
            <Trophy className="w-4 h-4" /> Ver Ranking Global
          </button>
        </div>
      </div>
    </div>
  );
}

const ACH_LEGACY = {
  primeira_jornada: { emoji: "🚀", nome: "Primeira Jornada" },
  mestre_estrategia: { emoji: "🧠", nome: "Mestre da Estratégia" },
  sem_gastar: { emoji: "💎", nome: "Sem Gastar" },
  visionario: { emoji: "🔥", nome: "Visionário" },
  sobrevivente: { emoji: "🛡️", nome: "Sobrevivente" },
};

function AchBadge({ id }) {
  const a = ACH_META[id] || ACH_LEGACY[id] || { emoji: "⭐", nome: id };
  return (
    <motion.div initial={{ scale: 0 }} animate={{ scale: 1 }} transition={{ type: "spring", stiffness: 300 }}
      className="flex flex-col items-center gap-1 p-3 rounded-2xl bg-white/5 border border-[#F59E0B]/30">
      <span className="text-2xl">{a.emoji}</span>
      <span className="text-[11px] text-center text-slate-300 leading-tight">{a.nome}</span>
    </motion.div>
  );
}
